import { ColDef } from "ag-grid-community";
import { Column } from "constants/columns";
import { ParentSaleTabs, ParentSaleTabType } from "constants/sale";

import { BaseColumnDefinitions } from "lib/agGrid/columnDefinitions";

// Basic info available for business users
const BASE_COLUMNS: Array<ColDef<SaleLot>> = [
  BaseColumnDefinitions[Column.AGENCY_NAME],
  BaseColumnDefinitions[Column.QUANTITY_DELIVERED],
  BaseColumnDefinitions[Column.PEN],
  BaseColumnDefinitions[Column.BUYER_WAY_NAME],
  BaseColumnDefinitions[Column.DESTINATION_PIC],
  BaseColumnDefinitions[Column.NVD],
  BaseColumnDefinitions[Column.VENDOR_NUM],
  BaseColumnDefinitions[Column.VENDOR_PIC],
  BaseColumnDefinitions[Column.WEIGHT],
  BaseColumnDefinitions[Column.AVERAGE_WEIGHT],
  BaseColumnDefinitions[Column.LOT_NUMBER_COMBINED],
  BaseColumnDefinitions[Column.QUANTITY],
  BaseColumnDefinitions[Column.DIFF],
  BaseColumnDefinitions[Column.BREED_NAME],
  BaseColumnDefinitions[Column.SEX_NAME],
  BaseColumnDefinitions[Column.AGE_GROUP_NAME],
  BaseColumnDefinitions[Column.CATEGORY_NAME],
  BaseColumnDefinitions[Column.GRADE_NAME],
  BaseColumnDefinitions[Column.SALE_ROUND_NAME],
  BaseColumnDefinitions[Column.STATUS],
];

// Basic info available for business users who are buyers
const BASE_BUYER_COLUMNS: Array<ColDef<SaleLot>> = [
  ...BASE_COLUMNS,
  BaseColumnDefinitions[Column.BUYER_NAME],
  BaseColumnDefinitions[Column.THIRD_PARTY_NAME],
];

const COMMERCIAL_BUYER_COLUMNS: Array<ColDef<SaleLot>> = [
  ...BASE_BUYER_COLUMNS,
  BaseColumnDefinitions[Column.TOTAL_PRICE],
  BaseColumnDefinitions[Column.UNIT_PRICE],
  {
    // We don't want the edit business popup being linked off here - just the name of the business.
    ...BaseColumnDefinitions[Column.INVOICE_TO_NAME],
    cellRenderer: null,
  },
];

const COMPLIANCE_BUYER_COLUMNS: Array<ColDef<SaleLot>> = [
  ...BASE_BUYER_COLUMNS,
  BaseColumnDefinitions[Column.VENDOR_NAME],
  BaseColumnDefinitions[Column.BUYER_SHORT_CODE],
  BaseColumnDefinitions[Column.BUYER_SHORT_CODE_SALEYARD],
  BaseColumnDefinitions[Column.SCANNED_COUNT],
  BaseColumnDefinitions[Column.SCANNED_PERCENTAGE],
  {
    // We don't want the edit business popup being linked off here - just the name of the business.
    ...BaseColumnDefinitions[Column.INVOICE_TO_NAME],
    cellRenderer: null,
  },
];

// Basic info available for business users who are vendors
const BASE_VENDOR_COLUMNS: Array<ColDef<SaleLot>> = BASE_COLUMNS;

const COMMERCIAL_VENDOR_COLUMNS: Array<ColDef<SaleLot>> = [
  ...BASE_VENDOR_COLUMNS,
  BaseColumnDefinitions[Column.TOTAL_PRICE],
  BaseColumnDefinitions[Column.UNIT_PRICE],
];

const COMPLIANCE_VENDOR_COLUMNS: Array<ColDef<SaleLot>> = [
  ...BASE_VENDOR_COLUMNS,
  BaseColumnDefinitions[Column.VENDOR_NAME],
  BaseColumnDefinitions[Column.BUYER_SHORT_CODE],
  BaseColumnDefinitions[Column.BUYER_SHORT_CODE_SALEYARD],
  BaseColumnDefinitions[Column.SCANNED_COUNT],
  BaseColumnDefinitions[Column.SCANNED_PERCENTAGE],
];

const BUSINESS_USER_PARENT_TAB_GENERAL_COLUMNS: Record<
  ParentSaleTabType,
  Array<ColDef>
> = {
  [ParentSaleTabs.BOUGHT]: BASE_BUYER_COLUMNS,
  [ParentSaleTabs.SOLD]: BASE_VENDOR_COLUMNS,
};

const BUSINESS_USER_PARENT_TAB_COMMERCIAL_COLUMNS: Record<
  ParentSaleTabType,
  Array<ColDef>
> = {
  [ParentSaleTabs.BOUGHT]: COMMERCIAL_BUYER_COLUMNS,
  [ParentSaleTabs.SOLD]: COMMERCIAL_VENDOR_COLUMNS,
};

const BUSINESS_USER_PARENT_TAB_COMPLIANCE_COLUMNS: Record<
  ParentSaleTabType,
  Array<ColDef>
> = {
  [ParentSaleTabs.BOUGHT]: COMPLIANCE_BUYER_COLUMNS,
  [ParentSaleTabs.SOLD]: COMPLIANCE_VENDOR_COLUMNS,
};

interface GetBusinessUserSaleReportsColumnsFunction {
  (
    isCommercialUser: boolean,
    isComplianceUser: boolean,
    parentSaleTab: ParentSaleTabType,
  ): Array<ColDef>;
}

export const getBusinessUserSaleReportsColumns: GetBusinessUserSaleReportsColumnsFunction =
  (isCommercialUser, isComplianceUser, parentSaleTab) => {
    if (isCommercialUser) {
      return BUSINESS_USER_PARENT_TAB_COMMERCIAL_COLUMNS[parentSaleTab] || [];
    }
    if (isComplianceUser) {
      return BUSINESS_USER_PARENT_TAB_COMPLIANCE_COLUMNS[parentSaleTab] || [];
    }
    return BUSINESS_USER_PARENT_TAB_GENERAL_COLUMNS[parentSaleTab] || [];
  };
